if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();

        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        /*
         * Listen for the Add to Cart form inside Quick Add.
         */
        this.addEventListener('submit', this.handleSubmit.bind(this));
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (cartNotification && this.openedBy) {
          if (typeof cartNotification.setActiveElement === 'function') {
            cartNotification.setActiveElement(this.openedBy);
          }
        }

        this.modalContent.innerHTML = '';

        if (preventFocus) {
          this.openedBy = null;
        }

        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        const spinner = opener.querySelector('.loading__spinner');

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            const productElement =
              responseHTML.querySelector('product-info');

            if (!productElement) return;

            this.preprocessHTML(productElement);

            HTMLUpdateUtility.setInnerHTML(
              this.modalContent,
              productElement.outerHTML
            );

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            this.modalContent
              .querySelector('product-component')
              ?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            if (spinner) {
              spinner.classList.add('hidden');
            }
          });
      }

      /*
       * ============================================================
       * QUICK ADD → CART DRAWER
       * ============================================================
       */

      async handleSubmit(event) {
        const form = event.target;

        if (!form || form.tagName !== 'FORM') return;

        /*
         * Make absolutely sure this is the Add to Cart form.
         */
        const action = form.getAttribute('action') || '';

        if (!action.includes('/cart/add')) return;

        /*
         * STOP Shopify from navigating to /cart.
         */
        event.preventDefault();
        event.stopPropagation();

        /*
         * Prevent double clicks.
         */
        if (form.classList.contains('quick-add-submitting')) {
          return;
        }

        form.classList.add('quick-add-submitting');

        const submitButton = form.querySelector(
          '[type="submit"]'
        );

        if (submitButton) {
          submitButton.classList.add('loading');
          submitButton.setAttribute(
            'aria-disabled',
            'true'
          );
        }

        try {
          const formData = new FormData(form);

          /*
           * Add product through Shopify AJAX Cart API.
           */
          const response = await fetch(
            `${window.Shopify.routes.root}cart/add.js`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: formData
            }
          );

          const data = await response.json();

          if (!response.ok) {
            throw new Error(
              data.description ||
              data.message ||
              'Unable to add product to cart.'
            );
          }

          /*
           * Product successfully added.
           *
           * Now refresh the existing Shopify cart drawer.
           */
          await this.refreshCartDrawer();

          /*
           * Close the Quick Add modal.
           */
          this.hide(true);

          /*
           * Open the cart drawer.
           */
          const cartDrawer =
            document.querySelector('cart-drawer');

          if (cartDrawer) {
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          }

        } catch (error) {
          console.error(
            'Quick Add Add-to-Cart error:',
            error
          );

          /*
           * Let Shopify's normal error message appear if
           * the product cannot be added.
           */
          let errorContainer =
            form.querySelector(
              '.product-form__error-message-wrapper'
            );

          if (errorContainer) {
            errorContainer.removeAttribute('hidden');

            const errorMessage =
              errorContainer.querySelector(
                '.product-form__error-message'
              );

            if (errorMessage) {
              errorMessage.textContent =
                error.message;
            }
          }

        } finally {
          form.classList.remove(
            'quick-add-submitting'
          );

          if (submitButton) {
            submitButton.classList.remove(
              'loading'
            );

            submitButton.removeAttribute(
              'aria-disabled'
            );
          }
        }
      }

      /*
       * ============================================================
       * REFRESH CART DRAWER
       * ============================================================
       */

      async refreshCartDrawer() {
        const cartDrawer =
          document.querySelector('cart-drawer');

        if (!cartDrawer) {
          return;
        }

        /*
         * Ask Shopify for the latest cart drawer HTML.
         */
        const response = await fetch(
          `${window.Shopify.routes.root}cart?sections=cart-drawer`,
          {
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            'Could not refresh cart drawer.'
          );
        }

        const sections = await response.json();

        const drawerHTML =
          sections['cart-drawer'];

        if (!drawerHTML) {
          return;
        }

        const parser = new DOMParser();

        const documentHTML =
          parser.parseFromString(
            drawerHTML,
            'text/html'
          );

        const newDrawer =
          documentHTML.querySelector(
            'cart-drawer'
          );

        if (!newDrawer) {
          return;
        }

        /*
         * Replace the contents of the existing
         * cart drawer while keeping the custom
         * cart-drawer element alive.
         */
        cartDrawer.innerHTML =
          newDrawer.innerHTML;

        /*
         * Remove empty state.
         */
        cartDrawer.classList.remove(
          'is-empty'
        );

        /*
         * Reinitialize Shopify cart functionality
         * if the theme exposes it.
         */
        if (
          typeof cartDrawer.bindEvents ===
          'function'
        ) {
          cartDrawer.bindEvents();
        }

        /*
         * Tell the rest of the theme that the cart
         * has changed.
         */
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true
          })
        );
      }

      /*
       * ============================================================
       * EXISTING QUICK ADD FUNCTIONS
       * ============================================================
       */

      preprocessHTML(productElement) {
        productElement.classList.forEach(
          (classApplied) => {
            if (
              classApplied.startsWith('color-') ||
              classApplied === 'gradient'
            ) {
              this.modalContent.classList.add(
                classApplied
              );
            }
          }
        );

        this.preventDuplicatedIDs(
          productElement
        );

        this.removeDOMElements(
          productElement
        );

        this.removeGalleryListSemantic(
          productElement
        );

        this.updateImageSizes(
          productElement
        );

        this.preventVariantURLSwitching(
          productElement
        );
      }

      preventVariantURLSwitching(
        productElement
      ) {
        productElement.setAttribute(
          'data-update-url',
          'false'
        );
      }

      removeDOMElements(productElement) {
        const pickupAvailability =
          productElement.querySelector(
            'pickup-availability'
          );

        if (pickupAvailability) {
          pickupAvailability.remove();
        }

        const productModal =
          productElement.querySelector(
            'product-modal'
          );

        if (productModal) {
          productModal.remove();
        }

        const modalDialog =
          productElement.querySelectorAll(
            'modal-dialog'
          );

        if (modalDialog) {
          modalDialog.forEach(
            (modal) => modal.remove()
          );
        }
      }

      preventDuplicatedIDs(productElement) {
        const sectionId =
          productElement.dataset.section;

        const oldId = sectionId;
        const newId =
          `quickadd-${sectionId}`;

        productElement.innerHTML =
          productElement.innerHTML.replaceAll(
            oldId,
            newId
          );

        Array.from(
          productElement.attributes
        ).forEach((attribute) => {
          if (
            attribute.value.includes(oldId)
          ) {
            productElement.setAttribute(
              attribute.name,
              attribute.value.replace(
                oldId,
                newId
              )
            );
          }
        });

        productElement.dataset.originalSection =
          sectionId;
      }

      removeGalleryListSemantic(
        productElement
      ) {
        const galleryList =
          productElement.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!galleryList) return;

        galleryList.setAttribute(
          'role',
          'presentation'
        );

        galleryList
          .querySelectorAll(
            '[id^="Slide-"]'
          )
          .forEach((li) => {
            li.setAttribute(
              'role',
              'presentation'
            );
          });
      }

      updateImageSizes(productElement) {
        const product =
          productElement.querySelector(
            '.product'
          );

        const desktopColumns =
          product?.classList.contains(
            'product--columns'
          );

        if (!desktopColumns) return;

        const mediaImages =
          product.querySelectorAll(
            '.product__media img'
          );

        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (
          product.classList.contains(
            'product--medium'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '605px'
            );
        } else if (
          product.classList.contains(
            'product--small'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '495px'
            );
        }

        mediaImages.forEach((img) => {
          img.setAttribute(
            'sizes',
            mediaImageSizes
          );
        });
      }
    }
  );
}